import React from 'react'
import classes from './MoveModal.module.scss';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { Modal, Tree, TreeProps } from 'antd';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { documnentIcons } from 'helpers/documentIcons';
import { TFunction } from 'i18next';
import api from 'api';
import { IFolder, IDocument } from 'interfaces';
import { handleMoveEntities } from 'store/reducers/documentsCreator';
import { useNavigate } from 'react-router';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getRightNameofEntity, isChildOfChosen, isParentOfChosen, isTheSame } from './moveHelpers';

interface DataNode {
  title: string;
  key: string;
  isLeaf?: boolean;
  children?: DataNode[];
  icon?: JSX.Element;
}

type MoveModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  isLoading: boolean;
}

const findInSubtree = (structure: DataNode[], targetKey: string) => {
  const root = structure[0];
  if (!root.children) return null;
  for (const topLevel of root.children) {
    if (topLevel.key === targetKey) {
      return topLevel.key;
    }
    if (topLevel.children && containsKey(topLevel.children, targetKey)) {        
      return topLevel.key;
    }
  }
  return null;
};

const containsKey = (nodes: DataNode[], targetKey: string) => {
  for (const node of nodes) {
    if (node.key === targetKey) return true;
    if (node.children && containsKey(node.children, targetKey)) return true;
  }
  return false;
};

const getTreeData = (t: TFunction, folders?: IFolder[], loading?: boolean): DataNode[] => [
  {
    title: t('Documents.modals.rootDir'),
    key: rootDirId,
    icon: <div>{documnentIcons['rootfolder' as keyof typeof documnentIcons]}</div>,
    children: folders?.map(folder => ({
      title: folder.name,
      key: folder.id!,
      icon: loading
        ? <LoadingOutlined className={classes.loadIcon}/>
        : <div>{documnentIcons['folder' as keyof typeof documnentIcons]}</div>,
      isLeaf: folder.subfolders && folder.subfolders[0] ? undefined : true,
    }))
  },
];

const rootDirId = 'null';

export default function MoveModal({
  isOpen,
  onCancel,
  isLoading,
}: MoveModalProps) {
  const [treeData, setTreeData] = React.useState<DataNode[]>([]);
  const [targetFolder, setTargetFolder] = React.useState<React.Key[]>([rootDirId]);
  const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>([rootDirId]);
  const [autoExpandParent, setAutoExpandParent] = React.useState<boolean>(true);
  const [targetFolderName, setTargetFolderName] = React.useState<string>(rootDirId);
  const [topLevelFolder, setTopLevelFolder] = React.useState<string | null>(null);

  const { foldersForPermissions } = useAppSelector((store) => store.permissions); 
  const { isOpenMoveEntityModal, messageApi } = useAppSelector((store) => store.windowState);
  const { currentFolder, entityOfContextMenu, folders, choosenDocuments } = useAppSelector((store) => store.documents);  
  const { dataRoom } = useAppSelector((store) => store.dataRoom);

  const { choosenFolderIds } = React.useMemo(() => {
    const choosenFolderIds = choosenDocuments
      .filter(({ key }) => (key as string).startsWith('folder'))
      .map(folder => String(folder.key)?.split(' ')[1]);
    return { choosenFolderIds: choosenFolderIds };
  }, [choosenDocuments]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();
  
  const nameOfEntity = getRightNameofEntity(choosenDocuments, entityOfContextMenu);
  const currentFolderId = currentFolder[0] ? currentFolder[currentFolder.length - 1]?.id : 'null';

  const isParentForAnyFolder = isParentOfChosen(
    treeData,
    choosenFolderIds[0] ? choosenFolderIds : [entityOfContextMenu?.id || ''],
    String(targetFolder)
  );

  const buildCorrectTreeData = async () => {
    if (currentFolderId === 'null') {
      const preparedFolders = foldersForPermissions.map(folder => ({
        ...folder,
        subfolders: folders.find(elem => elem.id === folder.id)?.subfolders
      }))
      setTreeData(getTreeData(t, preparedFolders));
    } else {
      setTreeData(getTreeData(t, foldersForPermissions.map(folder => ({ ...folder })), true));
      try {
        const rootFolders = await api.getFolders(dataRoom?.id!);
        const preparedFolders = foldersForPermissions.map(folder => ({
          ...folder,
          subfolders: rootFolders.data.find(elem => elem.id === folder.id)?.subfolders
        }))
        setTreeData(getTreeData(t, preparedFolders));
      } catch (e) { }
    }
  };
  
  React.useEffect(() => {
    foldersForPermissions[0] && !treeData[0] && buildCorrectTreeData();
  }, [foldersForPermissions]);

  React.useEffect(() => {
    if (isOpenMoveEntityModal) {
      setTargetFolder([rootDirId]);
      setExpandedKeys([rootDirId]);
      setTargetFolderName(t('Documents.modals.rootDir'));
    } else {
      setTargetFolder([]);
      setExpandedKeys([]);
      setTargetFolderName('');
      setTopLevelFolder(null);
    }
  }, [isOpenMoveEntityModal, entityOfContextMenu]);
  
  const onSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
    const foldersIDsToCheck = choosenFolderIds[0] ? choosenFolderIds : [entityOfContextMenu?.id || ''];
    const result = isChildOfChosen(treeData, foldersIDsToCheck, String(info.node.key));
    const isSame = isTheSame(foldersIDsToCheck, String(info.node.key));
    const isParent = isParentOfChosen(treeData, foldersIDsToCheck, String(info.node.key));
        
    if (isSame) {
      messageApi?.error(t('Documents.error.theSame'), 10);
      return;
    } else if (result) {
      messageApi?.error(t('Documents.error.childOfChosen'), 10);
      return;
    } else if (isParent) {
      messageApi?.error(t('Documents.error.itsParent'), 10);
      return;
    }

    const folderOfRoot = foldersForPermissions.find(folder => folder.id === selectedKeys[0]);

    if (folderOfRoot) {
      if (!folderOfRoot.permissions?.includes('can_upload')) {
        messageApi?.error(`${t('Documents.error.accessToMove')}"${folderOfRoot.name}"`);
        return;
      };
    };

    const choosenFolderLevel = info.node?.pos?.split('-')?.length - 2;
    if (choosenFolderLevel > 14 && nameOfEntity !== 'file') {
      messageApi?.warning(t('Documents.management.max15level', {level: 14+1}), 12)
      return;
    }

    setTargetFolderName(String(info.node.key === 'null' ? t('Documents.modals.rootDir') : info.node.title));
    setTargetFolder(selectedKeys);
    setTopLevelFolder(findInSubtree(treeData, String(selectedKeys[0])));
  };

  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };

  const onLoadData = async ({ key, children }: any) => {
    if (children) {
      return;
    };

    const folderOfRoot = foldersForPermissions.find(folder => folder.id === key);

    if (folderOfRoot) {
      if (!folderOfRoot.permissions?.includes('can_upload')) {
        messageApi?.error(`${t('Documents.error.accessToMove')}"${folderOfRoot.name}"`)
        return;
      };
    };
    
    const { data: childrenFolders } = await api.getFoldersOfFolder(key);
    if (childrenFolders.length === 0) {
      setTreeData((origin) =>
        updateTreeData(origin, key, [], true),
      );
      return;
    };

    const childrenElements = childrenFolders.map(folder => ({
      title: folder.name,
      key: folder.id!,
      icon: <div>{documnentIcons['folder' as keyof typeof documnentIcons]}</div>,
      isLeaf: folder.subfolders && folder.subfolders[0] ? undefined : true,
    }));

    setTreeData((origin) =>
      updateTreeData(origin, key, childrenElements),
    );
  };

  // It's just a simple demo. You can use tree map to optimize update perf.
  const updateTreeData = (list: DataNode[], key: React.Key, children: DataNode[], isLeaf?: boolean): DataNode[] =>
    list.map((node) => {      
      if (node.key === key) {
        return {
          ...node,
          children,
          isLeaf: isLeaf !== undefined ? isLeaf : (children.length === 0 ? true : undefined),
        };
      }
      if (node.children) {
        return {
          ...node,
          children: updateTreeData(node.children, key, children, isLeaf),
        };
      }
      return node;
    });

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      centered
      okText={t('Documents.contextMenu.move')}
      cancelText={t('Documents.management.cancel')}
      okButtonProps={{disabled: isParentForAnyFolder}}
      confirmLoading={isLoading}
      title={t('Documents.modals.movingFile', { object: t(`Documents.modals.${nameOfEntity}`) })}
      onOk={() => {
        const isFromContextMenu = Boolean(entityOfContextMenu);
        const isContextEntitySelected = isFromContextMenu && choosenDocuments.some(d => d.key === entityOfContextMenu?.key);
        const selectedFiles = (choosenDocuments.length > 0 && (!isFromContextMenu || isContextEntitySelected))
          ? choosenDocuments.map(d => (d.key as string).split(' ')[(d.key as string).startsWith('folder') ? 1 : 2])
          : [entityOfContextMenu?.id!];

        dispatch(handleMoveEntities({
          selectedFiles: selectedFiles,
          folder_id: [String(targetFolder[0])],
          targetFolder: targetFolderName,
          t,
          navigate,
          topLevelFolder: String(topLevelFolder)
        }))
      }}
      className={cn(classes.modalMove, getTheme())}
    >
      <div className={classes.moveWrap}>
        <Tree
          loadData={onLoadData}
          treeData={treeData}
          selectedKeys={targetFolder}
          showIcon={true}
          onSelect={onSelect}
          autoExpandParent={autoExpandParent}
          onExpand={onExpand}
          expandedKeys={expandedKeys}
        />
      </div>

      { isLoading &&
        <UploadingComponent />
      }
    </Modal>
  )
};
